import { useEffect, useState } from 'react';
import { fetchProfile } from '../api/profileApi';
import { useAuth } from '../auth/AuthContext';
import { getSessionToken, getStoredLoginIdentifier } from '../auth/sessionToken';
import { ProfileBookingAuthBlockPanel } from './ProfileBookingAuthBlockPanel';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { RegisterTypeMenu } from '../regsiter/RegisterTypeMenu';
import { extractProfileDisplaySummary } from '../utils/profileDisplaySummary';
import {
  resolveBookingProfileAuthBlockReason,
  type BookingProfileAuthBlockReason,
} from '../utils/bookingProfileGateAuthError';
import {
  isPrivateClientProfileReadyForBooking,
  missingPrivateClientFieldsForBooking,
  privateClientCompletionPrefill,
  type PrivateClientBookingField,
} from '../utils/profileRegistrationComplete';
import styles from './ProfileCompletionSheet.module.css';

type ProfileCompletionSheetProps = {
  onCompleted: () => void;
  onDismiss: () => void;
  /** When true, never auto-continue after GET /profile — user must submit (avoids pay-retry loops). */
  requireExplicitSubmit?: boolean;
};

export function ProfileCompletionSheet({
  onCompleted,
  onDismiss,
  requireExplicitSubmit = false,
}: ProfileCompletionSheetProps) {
  const { t } = useI18n();
  const { userName, logout } = useAuth();
  const { openAuth } = useAppNavigation();
  const [initialPayload, setInitialPayload] = useState<Record<string, unknown> | null>(null);
  const [missingFields, setMissingFields] = useState<PrivateClientBookingField[]>([]);
  const [authBlockReason, setAuthBlockReason] = useState<BookingProfileAuthBlockReason | null>(null);

  const signInAgain = () => {
    onDismiss();
    logout();
    openAuth('login');
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profileData = await fetchProfile();
        if (cancelled) {
          return;
        }
        const summary = extractProfileDisplaySummary(
          profileData,
          userName,
          getStoredLoginIdentifier(),
          getSessionToken(),
        );
        const loginId = getStoredLoginIdentifier();
        const missing = missingPrivateClientFieldsForBooking(profileData, summary, loginId);
        const ready = isPrivateClientProfileReadyForBooking(profileData, summary, loginId);
        if (missing.length === 0 && ready && !requireExplicitSubmit) {
          onCompleted();
          return;
        }
        setMissingFields(missing);
        setInitialPayload(privateClientCompletionPrefill(profileData, summary, loginId));
      } catch (err) {
        if (cancelled) {
          return;
        }
        const authReason = resolveBookingProfileAuthBlockReason(err);
        if (authReason) {
          setAuthBlockReason(authReason);
          return;
        }
        setMissingFields(missingPrivateClientFieldsForBooking(null, null));
        setInitialPayload(privateClientCompletionPrefill(null, null));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onCompleted, requireExplicitSubmit, userName]);

  return (
    <div className={styles.backdrop} role="presentation" onClick={onDismiss}>
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-completion-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 id="profile-completion-title" className={styles.title}>
            {authBlockReason ? t('bookings.error.profileAuth.title') : t('profile.completion.title')}
          </h2>
          {!authBlockReason ? (
            <p className={styles.intro}>{t('profile.completion.intro')}</p>
          ) : null}
          <button type="button" className={styles.close} onClick={onDismiss} aria-label={t('common.close')}>
            ×
          </button>
        </header>
        <div className={styles.body}>
          {authBlockReason ? (
            <ProfileBookingAuthBlockPanel
              reason={authBlockReason}
              onDismiss={onDismiss}
              onSignInAgain={signInAgain}
            />
          ) : initialPayload ? (
            <RegisterTypeMenu
              privateClientCompletionMode
              completionMissingFields={missingFields}
              initialPayloadOverride={initialPayload}
              onCompleted={onCompleted}
            />
          ) : (
            <p className={styles.loading}>{t('profile.completion.loading')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
